import React from 'react'

import Modal from 'react-modal'

import { getPaymentMethodDisplayName, getPaymentMethodIcon } from '@src/pages/FiatExchange/utils/paymentMethodUtils'
import { PaymentMethod } from '@src/types/coinbase'

interface SelectPaymentMethodModalProps {
    /** Controls visibility of the modal */
    isOpen: boolean
    /** Callback to close the modal */
    onClose: () => void
    /** The currently selected payment method (if any) */
    selectedPaymentMethod?: PaymentMethod
    /** Called when the user picks a payment method */
    setSelectedPaymentMethod: (method: PaymentMethod) => void
    /** List of available payment methods */
    paymentMethods: PaymentMethod[]
    /** Ramp direction (ONRAMP or OFFRAMP) */
    rampDirection?: 'ONRAMP' | 'OFFRAMP'
    /** Fiat currency symbol for onramp */
    fiatCurrencySymbol?: string
    /** Token symbol for offramp */
    tokenSymbol?: string
}

/**
 * Displays a list of payment methods and handles selection
 */
function PaymentMethodResultsDisplay({
    methods,
    onMethodClicked,
    selectedMethod,
    rampDirection = 'ONRAMP',
    fiatCurrencySymbol,
    tokenSymbol,
}: {
    methods: PaymentMethod[]
    onMethodClicked: (method: PaymentMethod) => void
    selectedMethod?: PaymentMethod
    rampDirection?: 'ONRAMP' | 'OFFRAMP'
    fiatCurrencySymbol?: string
    tokenSymbol?: string
}) {
    if (methods.length === 0) {
        return <p className='mt-4 text-center text-sm text-token-modal-label-text'>No payment methods available.</p>
    }

    return (
        <div className='flex flex-col gap-2'>
            {methods.map(method => {
                const isSelected = method.id === selectedMethod?.id
                return (
                    <button
                        key={method.id}
                        onClick={() => onMethodClicked(method)}
                        className={`flex items-center gap-3 rounded-md px-4 py-3.5 transition-colors duration-200 hover:bg-token-modal-chain-hover ${
                            isSelected ? 'border border-token-modal-border bg-token-modal-chain-selected' : ''
                        } `}
                    >
                        <div className='mr-3 h-6 w-6 flex-shrink-0'>
                            {getPaymentMethodIcon(method, {
                                width: 24,
                                height: 24,
                                className: 'text-token-modal-label-text',
                            })}
                        </div>
                        <div className='flex flex-col items-start'>
                            <span className='text-sm'>{getPaymentMethodDisplayName(method)}</span>
                            {method.description && (
                                <span className='text-xs text-token-modal-label-text'>{method.description}</span>
                            )}
                            {method.limits && method.limits.length > 0 && (
                                <span className='text-xs text-token-modal-label-text'>
                                    {(() => {
                                        // Determine which symbol to use
                                        const symbol =
                                            rampDirection === 'ONRAMP'
                                                ? fiatCurrencySymbol || method.limits[0].currency
                                                : tokenSymbol || method.limits[0].currency

                                        return (
                                            <>
                                                Min:{' '}
                                                {method.limits[0].min ? `${method.limits[0].min} ${symbol}` : 'N/A'} |
                                                Max:{' '}
                                                {method.limits[0].max ? `${method.limits[0].max} ${symbol}` : 'N/A'}
                                            </>
                                        )
                                    })()}
                                </span>
                            )}
                        </div>
                    </button>
                )
            })}
        </div>
    )
}

/**
 * Modal for selecting a payment method
 */
const SelectPaymentMethodModal: React.FC<SelectPaymentMethodModalProps> = ({
    isOpen,
    onClose,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentMethods,
    rampDirection = 'ONRAMP',
    fiatCurrencySymbol,
    tokenSymbol,
}) => {
    // Modal styles – mirroring the token modal styles from SelectFiatModal
    const modalStyle = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--token-modal-overlay)',
            zIndex: 50,
            padding: '16px',
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: '576px',
            height: '680px',
            padding: 0,
            border: '1px solid var(--token-modal-border)',
            borderRadius: '0.375rem',
            background: 'var(--token-modal-background)',
            overflow: 'hidden',
        },
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyle} ariaHideApp={false}>
            {/* MOBILE VIEW */}
            <div className='flex h-full flex-col sm:hidden'>
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select Payment Method</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Payment method list */}
                <div className='flex-1 overflow-y-auto px-4'>
                    <PaymentMethodResultsDisplay
                        methods={paymentMethods}
                        onMethodClicked={method => {
                            setSelectedPaymentMethod(method)
                            onClose()
                        }}
                        selectedMethod={selectedPaymentMethod}
                        rampDirection={rampDirection}
                        fiatCurrencySymbol={fiatCurrencySymbol}
                        tokenSymbol={tokenSymbol}
                    />
                </div>
            </div>

            {/* DESKTOP VIEW */}
            <div className='hidden h-full flex-col sm:flex'>
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select Payment Method</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Payment method list (scrollable) */}
                <div className='flex-1 overflow-y-auto px-4 pb-4 pt-4'>
                    <PaymentMethodResultsDisplay
                        methods={paymentMethods}
                        onMethodClicked={method => {
                            setSelectedPaymentMethod(method)
                            onClose()
                        }}
                        selectedMethod={selectedPaymentMethod}
                        rampDirection={rampDirection}
                        fiatCurrencySymbol={fiatCurrencySymbol}
                        tokenSymbol={tokenSymbol}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default SelectPaymentMethodModal
